import { api, type ApiResponse } from '../utils';

/**
 * Phase 3.3: Interactive Search/Browse Services
 */

export interface SearchProfilesParams {
  query?: string;
  objectives?: string[];
  industries?: string[];
  urgency?: 'urgent' | 'time_sensitive' | 'ongoing' | 'exploratory';
  page?: number;
  limit?: number;
}

export interface AnonymousProfile {
  id: string;
  display_name: string;
  profile_summary: string;
  objectives: string[];
  // Jun-16 (a user V1 test, O3): null when the user never set a timeline. The
  // cards omit the badge instead of defaulting to "Ongoing".
  urgency: string | null;
  freshness_score: number;
  member_since: string;
  last_active_at: string;
  compatibility_hint?: number;
}

export interface ExpressInterestParams {
  to_user_id: string;
  message?: string;
}

export interface InterestResponse {
  interest_id: string;
  status: 'pending' | 'mutual' | 'declined' | 'expired';
  match_created?: boolean;
  match_id?: string;
}

export interface BrowseProfileParams {
  viewed_user_id: string;
  view_duration_seconds?: number;
  source?: string;
}

export interface MyInterestsParams {
  type?: 'sent' | 'received' | 'mutual';
  page?: number;
  limit?: number;
}

const searchProfiles = async (
  params: SearchProfilesParams
): Promise<ApiResponse> => {
  const queryParams = new URLSearchParams();

  if (params.query) queryParams.set('query', params.query);
  if (params.objectives?.length) queryParams.set('objectives', params.objectives.join(','));
  if (params.industries?.length) queryParams.set('industries', params.industries.join(','));
  if (params.urgency) queryParams.set('urgency', params.urgency);
  if (params.page) queryParams.set('page', params.page.toString());
  if (params.limit) queryParams.set('limit', params.limit.toString());

  const { data: response } = await api.get(`/discover/search?${queryParams.toString()}`);
  return response;
};

const expressInterest = async (
  params: ExpressInterestParams
): Promise<ApiResponse> => {
  const { data: response } = await api.post('/discover/interest', params);
  return response;
};

const recordBrowse = async (
  params: BrowseProfileParams
): Promise<ApiResponse> => {
  const { data: response } = await api.post('/discover/browse', params);
  return response;
};

const getMyInterests = async (
  params: MyInterestsParams
): Promise<ApiResponse> => {
  const queryParams = new URLSearchParams();

  if (params.type) queryParams.set('type', params.type);
  if (params.page) queryParams.set('page', params.page.toString());
  if (params.limit) queryParams.set('limit', params.limit.toString());

  const { data: response } = await api.get(`/discover/my-interests?${queryParams.toString()}`);
  return response;
};

export {
  searchProfiles,
  expressInterest,
  recordBrowse,
  getMyInterests,
};
